import React, { useState, useEffect } from 'react';
import {
    Typography, Box, Button, IconButton, Tooltip, Grid,
    useTheme, useMediaQuery, TextField, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Switch,
    MenuItem, CircularProgress, FormControl, InputLabel, Select
} from '@mui/material';
import { FiBook, FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../api';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import SubjectModal from './SubjectModal';


//reusable subjectFormField
const SubjectFormFields = ({ subjectData, setSubjectData, exams, generateCode,
    codeManuallyEdited,
    setCodeManuallyEdited }) => {
    return (
        <>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="exam-select-label">Select Exam</InputLabel>
                <Select
                    labelId="exam-select-label"
                    value={subjectData.exam_id}
                    label="Select Exam"
                    onChange={(e) => setSubjectData({ ...subjectData, exam_id: e.target.value })}
                >
                    {exams.map((exam) => (
                        <MenuItem key={exam.id} value={exam.id}>
                            {exam.title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* ✅ Show Exam ID as a disabled field */}
            {/* <TextField
                fullWidth
                label="Exam ID"
                margin="dense"
                value={subjectData.exam_id}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
            /> */}

            <TextField
                fullWidth
                label="Subject Name"
                margin="dense"
                value={subjectData.name}
                onChange={(e) => {
                    const updatedName = e.target.value;
                    setSubjectData((prev) => ({
                        ...prev,
                        name: updatedName,
                        // Only auto-generate code if not manually edited
                        code: !prev.code || !codeManuallyEdited ? generateCode(updatedName, exams, prev.code) : prev.code
                    }));
                }}
            />
            <TextField
                fullWidth
                label="Code"
                margin="dense"
                value={subjectData.code}
                onChange={(e) => {
                    const newCode = e.target.value;
                    setSubjectData((prev) => ({
                        ...prev,
                        code: newCode
                    }));

                    // Safe check before calling setCodeManuallyEdited
                    if (typeof setCodeManuallyEdited === 'function') {
                        setCodeManuallyEdited(true);
                    }
                }}
            />

            <TextField
                select
                fullWidth
                label="Status"
                margin="dense"
                value={subjectData.status}
                onChange={(e) => setSubjectData({ ...subjectData, status: e.target.value })}
            >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
        </>
    );
};


const SubjectList = () => {
    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [newSubject, setNewSubject] = useState({
        exam_id: '',
        name: '',
        code: '',
        status: 'Active'
    });
    const [toastOpen, setToastOpen] = useState(false);
    const [error, setError] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
    const generateCode = (subjectName) => {
        if (!subjectName) return '';

        const prefix = subjectName
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 4);

        let code;
        let counter = 1;

        do {
            code = `${prefix}${String(counter).padStart(3, '0')}`;
            counter++;
        } while (subjects.some(sub => sub.code.toLowerCase() === code.toLowerCase()));

        return code;
    };



    useEffect(() => {
        const loadData = async () => {
            try {
                const res = await api.get('/exams');
                setExams(res.data);

                // ✅ Move this inside to guarantee exams are set first
                const subjectsRes = await api.get('/subjects');
                const subjectsData = subjectsRes.data;

                const transformed = subjectsData.map((sub) => {
                    const exam = res.data.find((e) => e.id === sub.exam_id);
                    return {
                        id: sub.id,
                        name: sub.name,
                        code: sub.code,
                        status: sub.status ? 'Active' : 'Inactive',
                        exam_id: sub.exam_id,
                        exam_name: exam?.title || 'Unknown'
                    };
                });

                setSubjects(transformed);
            } catch (err) {
                console.error('Failed to fetch data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);



    useEffect(() => {
        if (exams.length > 0) {
            fetchSubjects(); // only run when exams are loaded
        }
    }, [exams]);

    // ✅ SweetAlert after toastMessage update
    useEffect(() => {
        if (toastMessage) {
            Swal.fire('Success', toastMessage, 'success');
            setToastMessage('');
        }
    }, [toastMessage]);

    const fetchExams = async () => {
        console.log(subjects[0]);

        try {
            const res = await api.get('/exams');
            console.log('Exams Response:', res.data);
            setExams(res.data);
            await fetchSubjects();  //call here once exams are definitely fetched
        } catch (err) {
            console.error('Failed to fetch exams', err);
        }
    };

    const fetchSubjects = async () => {
        try {
            const response = await api.get('/subjects');
            console.log('Subjects API:', response.data);
            console.log('Exams at this point:', exams);

            const transformed = response.data.map((sub) => {
                const exam = exams.find((e) => e.id === sub.exam_id);
                return {
                    id: sub.id,
                    name: sub.name,
                    code: sub.code,
                    status: sub.status ? 'Active' : 'Inactive',
                    exam_id: sub.exam_id,
                    exam_name: exam?.title || 'Unknown'
                };
            });

            setSubjects(transformed);
        } catch (err) {
            console.error('Failed to fetch subjects', err);
        } finally {
            setLoading(false);
        }
    };



    const handleAddSubject = async () => {
        if (!newSubject.exam_id || !newSubject.name || !newSubject.code) {
            setError('Please fill all fields');
            return;
        }

        const codeExists = subjects.some(
            (sub) =>
                sub.exam_id === parseInt(newSubject.exam_id) &&
                sub.code.toLowerCase() === newSubject.code.toLowerCase()
        );
        const nameExists = subjects.some(
            (sub) =>
                sub.exam_id === parseInt(newSubject.exam_id) &&
                sub.name.toLowerCase() === newSubject.name.toLowerCase()
        );


        if (codeExists || nameExists) {
            let errorMessage = 'Subject already exists with:';
            if (codeExists) errorMessage += `\n- Code: "${newSubject.code}"`;
            if (nameExists) errorMessage += `\n- Name: "${newSubject.name}"`;

            Swal.fire({
                title: 'Duplicate Entry',
                text: errorMessage,
                icon: 'warning'
            });
            return;
        }


        try {
            const payload = {
                exam_id: parseInt(newSubject.exam_id),
                name: newSubject.name,
                code: newSubject.code,
                status: newSubject.status === 'Active' ? 1 : 0
            };
            const res = await api.post('/subjects', payload);
            const addedSubject = res.data.subject;

            const exam = exams.find((e) => e.id === addedSubject.exam_id);

            const newSub = {
                id: addedSubject.id,
                name: addedSubject.name,
                code: addedSubject.code,
                status: addedSubject.status ? 'Active' : 'Inactive',
                exam_id: addedSubject.exam_id,
                exam_name: exam?.title || 'Unknown' // ✅ add this
            };

            setSubjects(prev => [...prev, newSub]);
            setAddModalOpen(false);
            setNewSubject({ exam_id: '', name: '', code: '', status: 'Active' });
            Swal.fire('Success', 'Subject added!', 'success');
        } catch (err) {
            console.error(err);
            if (err.response?.data?.message?.includes('already exists')) {
                Swal.fire('Error', 'Subject already exists!', 'error');
            } else {
                Swal.fire('Error', 'Failed to add subject', 'error');
            }
        }
    };

    //function to handle delete 
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: 'You will not be able to recover this subject!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        });

        if (confirm.isConfirmed) {
            try {
                await api.delete(`/subjects/${id}`);

                // Optimistically update the UI
                setSubjects(prev => prev.filter(subject => subject.id !== id));

                Swal.fire('Deleted!', 'Subject has been deleted.', 'success');
            } catch (err) {
                console.error(err);
                Swal.fire('Error', 'Failed to delete subject', 'error');
            }
        }
    };


    //function to handle toggle of actions
    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 'Active' ? 0 : 1;

        try {
            const res = await api.put(`/subjects/${id}`,
                { status: newStatus });

            if (res.status === 200 || res.status === 201) {
                setSubjects(prev =>
                    prev.map(sub =>
                        sub.id === id ? { ...sub, status: newStatus ? 'Active' : 'Inactive' } : sub
                    )
                );
                Swal.fire('Success', `Subject marked as ${newStatus ? 'Active' : 'Inactive'}`, 'success');
            }
        } catch (err) {
            console.error('Failed to toggle status', err);
            Swal.fire('Error', 'Could not update status', 'error');
        }
    };


    //function to handle edit
    const handleEdit = (row) => {
        setEditData(row);
        setEditModalOpen(true);
    };

    const handleSaveChanges = async () => {
        try {
            const payload = {
                id: editData.id, // Important for identifying which subject to update
                exam_id: parseInt(editData.exam_id),
                name: editData.name,
                code: editData.code,
                status: editData.status === 'Active' ? 1 : 0
            };

            const duplicate = subjects.find(
                (sub) =>
                    sub.id !== editData.id &&
                    sub.exam_id === parseInt(editData.exam_id) &&
                    (sub.name.toLowerCase() === editData.name.toLowerCase() ||
                        sub.code.toLowerCase() === editData.code.toLowerCase())
            );

            if (duplicate) {
                Swal.fire('Duplicate Entry', 'A subject with the same name or code already exists for this exam.', 'warning');
                return;
            }

            const res = await api.put(`/subjects/${editData.id}`, payload);

            if (res.status === 200 || res.status === 201) {
                // ✅ lookup exam name again
                const exam = exams.find(e => e.id === payload.exam_id);

                const updatedSubject = {
                    ...editData,
                    exam_name: exam?.title || 'Unknown',
                    status: payload.status === 1 ? 'Active' : 'Inactive'
                };

                setSubjects(prev =>
                    prev.map(sub => (sub.id === editData.id ? updatedSubject : sub))
                );
                setEditModalOpen(false);

                // ✅ Success alert
                Swal.fire('Success', 'Subject updated successfully', 'success');

            }
        } catch (err) {
            console.error(err);
            // ❌ Error alert
            Swal.fire('Error', 'Failed to update subject', 'error');
        }
    };

    const filteredSubjects = subjects.filter(sub =>
        sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const columns = [
        // {
        //     field: 'exam_id',
        //     headerName: 'Exam ID',
        //     flex: 0.5,
        // },
        {
            field: 'id',
            headerName: 'Subject ID',
            flex: 0.5,
        },
        {
            field: 'exam_name',
            headerName: 'Exam Name',
            flex: 1,
        },

        { field: 'name', headerName: 'Subject Name', flex: 1 },
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'status', headerName: 'Status', flex: 0.7 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <Switch
                        checked={params.row.status === 'Active'}
                        onChange={() => handleStatusToggle(params.row.id, params.row.status)}
                        color="primary"
                    />
                    <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                            ✏️
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
                            <FiTrash2 />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarExport />
        </GridToolbarContainer>
    );

    return (
        <Box p={2} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
                mb={3}
                display="flex"
                flexDirection={isSmall ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                textAlign={isSmall ? 'center' : 'left'}
            >
                {/* Back */}
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        borderRadius: '12px',
                        p: '8px 12px',
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                            transform: 'scale(1.05)'
                        }
                    }}
                >
                    <FiArrowLeft />
                    {!isSmall && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
                </IconButton>

                {/* Title */}
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
                    <Box sx={{
                        fontSize: '2rem',
                        background: '#e3f2fd',
                        borderRadius: '50%',
                        p: 1,
                        color: '#1976d2',
                        animation: 'pulseIcon 1.6s infinite',
                    }}>
                        <FiBook />
                    </Box>
                    <Typography
                        variant={isSmall ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            px: 1
                        }}
                    >
                        Manage Subjects
                    </Typography>
                </Box>

                {/* Add */}
                <Button
                    fullWidth={isSmall}
                    variant="contained"
                    color="warning"
                    startIcon={<FiPlus />}
                    onClick={() => {
                        setAddModalOpen(true);
                        setCodeManuallyEdited(false); // ✅ Reset flag here
                    }}

                >
                    Add Subject
                </Button>
            </Box>

            {/* Table */}
            <Box height={800} sx={{ flexGrow: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={filteredSubjects}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        pagination
                        onSelectionModelChange={(ids) => setSelectedRows(ids)}
                        components={{ Toolbar: CustomToolbar }}
                        loading={loading}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        sx={{
                            minHeight: 0,
                            fontFamily: 'Segoe UI',
                            border: '1px solid #ddd',
                            boxShadow: 2,
                            '& .MuiDataGrid-columnHeaders': {
                                background: 'linear-gradient(to right, #e0f3ff, #d5faff)',
                                color: '#004c99',
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#fef7e0'
                            },
                            '& .MuiDataGrid-cell': {
                                fontSize: '0.95rem'
                            }
                        }}
                    />
                )}
            </Box>

            {/* Add Modal */}
            <SubjectModal
                open={addModalOpen}
                onClose={() => {
                    setAddModalOpen(false);
                    setNewSubject({ exam_id: '', name: '', code: '', status: 'Active' });
                }}
                onSubmit={handleAddSubject}
                subjectData={newSubject}
                setSubjectData={setNewSubject}
                exams={exams}
                generateCode={generateCode}
                codeManuallyEdited={codeManuallyEdited}
                setCodeManuallyEdited={setCodeManuallyEdited}
                title="Add New Subject"
                submitButtonText="Add Subject"
                submitButtonColor="success"
            />



            {/* Edit Modal */}
            <SubjectModal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setEditData(null);
                }}
                onSubmit={handleSaveChanges}
                subjectData={editData}
                setSubjectData={setEditData}
                exams={exams}
                generateCode={generateCode}
                codeManuallyEdited={codeManuallyEdited}
                setCodeManuallyEdited={setCodeManuallyEdited}
                title="Edit Subject"
                submitButtonText="Save Changes"
                submitButtonColor="primary"
            />

        </Box>
    );
};

export default SubjectList;
